import React from "react";
import { motion } from "framer-motion";
import { FaGithub, FaLinkedin, FaEnvelope } from "react-icons/fa";

const ContactLinks = () => (
  <div className="flex flex-col md:flex-row items-center justify-center gap-4 mt-2">
    <a
      href="https://github.com/Vishaljindher"
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 px-8 py-3 bg-[#FAF6EF] border border-[#E6D8C3] rounded-xl shadow hover:bg-[#EFE1CC]"
    >
      <FaGithub /> GitHub
    </a>

    <a
      href="https://linkedin.com/in/vishal-jindher-"
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 px-8 py-3 bg-[#FAF6EF] border border-[#E6D8C3] rounded-xl shadow hover:bg-[#EFE1CC]"
    >
      <FaLinkedin /> LinkedIn
    </a>

    <a
      href="mailto:yogeshjinder117@gmail.com"
      className="flex items-center gap-3 px-8 py-3 bg-[#FAF6EF] border border-[#E6D8C3] rounded-xl shadow hover:bg-[#EFE1CC]"
    >
      <FaEnvelope /> Email
    </a>
  </div>
);



export default function About() {
  return (
    <div className="min-h-screen bg-[#FAF6EF] text-[#3A2A1A] overflow-x-hidden">
      
      {/* ================= HERO ================= */}
      <section className="px-8 py-16 text-center">
        <motion.h1
          className="text-4xl md:text-5xl font-bold mb-4"
          initial={{ y: -40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.7 }}
        >
          About Us
        </motion.h1>

        <motion.h2
          className="text-xl md:text-2xl font-semibold text-[#7A4A28] mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          BharatParikhsa – Learn. Test. Share. Grow.
        </motion.h2>

        <motion.p
          className="max-w-3xl mx-auto text-lg text-[#5C4632]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          Empowering India through smarter, open, and community-driven online testing.
        </motion.p>
      </section>

<ContactLinks />


      {/* ================= INTRO ================= */}
      <section className="px-8 py-10">
        <div className="max-w-5xl mx-auto bg-[#F4EBDD] border border-[#E6D8C3] rounded-2xl p-10 shadow-lg space-y-6">
          <p className="text-lg text-[#5C4632]">
            BharatParikhsa is a modern online test-taking platform built with one clear goal:
            to make learning, practice, and assessment accessible to everyone.
          </p>

          <p className="text-lg text-[#5C4632]">
            We believe that knowledge grows faster when it is tested, shared, and practiced together.
            That belief is at the core of everything we build.
          </p>
        </div>
      </section>

      {/* ================= WHO WE ARE ================= */}
      <section className="px-8 py-14">
        <motion.h2
          className="text-3xl font-bold mb-10 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          Who We Are
        </motion.h2>

        <div className="max-w-5xl mx-auto space-y-6 text-[#5C4632] text-lg">
          <p>
            BharatParikhsa is a free and open testing platform where users can:
          </p>

          <ul className="list-disc pl-6 space-y-2">
            <li>Create their own online tests</li>
            <li>Take tests created by others</li>
            <li>Share tests with friends, classmates, or communities</li>
          </ul>

          <p>
            No heavy fees. No unnecessary restrictions.
            Just learning and evaluation — simple and effective.
          </p>
        </div>
      </section>

      {/* ================= MISSION & VISION ================= */}
      <section className="px-8 py-14 bg-[#F4EBDD] border-y border-[#E6D8C3]">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-4">

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h3 className="text-2xl font-bold mb-4 text-[#7A4A28]">Our Mission</h3>
            <p className="text-lg text-[#5C4632]">
              To democratize online testing in India by giving everyone the power
              to create, attempt, and share knowledge freely.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h3 className="text-2xl font-bold mb-4 text-[#7A4A28]">Our Vision</h3>
            <p className="text-lg text-[#5C4632]">
              To become India’s most trusted community-driven test platform where
              learning becomes measurable, meaningful, and pressure-free.
            </p>
          </motion.div>

        </div>
      </section>

      {/* ================= WHAT WE OFFER ================= */}
      <section className="px-8 py-20">
        <motion.h2
          className="text-3xl font-bold text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          What BharatParikhsa Offers
        </motion.h2>

        <div className="grid md:grid-cols-2 gap-10 max-w-6xl mx-auto">

          {[
            {
              title: "📝 Create Online Tests",
              desc: "Create exams, quizzes, revision tests, and topic-based assessments with zero complexity.",
            },
            {
              title: "🎯 Take Tests Freely",
              desc: "Practice without limits. Attempt tests multiple times and improve through repetition.",
            },
            {
              title: "🔗 Share With Friends",
              desc: "Share tests via links and use them in classrooms, groups, or communities.",
            },
            {
              title: "📊 Skill Improvement",
              desc: "Identify weak areas, improve speed and accuracy, and build exam confidence.",
            },
          ].map((item, i) => (
            <motion.div
              key={i}
              whileHover={{ y: -6 }}
              className="bg-[#F4EBDD] border border-[#E6D8C3] rounded-2xl p-8 shadow-md"
            >
              <h3 className="text-xl font-semibold mb-3 text-[#7A4A28]">
                {item.title}
              </h3>
              <p className="text-[#5C4632] text-lg">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= CORE VALUES ================= */}
      <section className="px-8 py-24 bg-[#F4EBDD] border-t border-[#E6D8C3]">
        <h2 className="text-3xl font-bold text-center mb-12">Our Core Values</h2>

        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6 text-lg text-[#5C4632]">
          <p>✔ Accessibility – Learning tools for everyone</p>
          <p>✔ Simplicity – Less friction, better learning</p>
          <p>✔ Community – Knowledge grows when shared</p>
          <p>✔ Fairness – Equal opportunity to improve</p>
          <p>✔ Growth Mindset – Practice leads to progress</p>
        </div>
      </section>

{/* ================= FUTURE ROADMAP ================= */}
<section className="px-8 py-24">
  <motion.h2
    className="text-3xl font-bold text-center mb-14"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
  >
    What’s Coming Next 🚀
  </motion.h2>

  <div className="grid md:grid-cols-2 gap-10 max-w-6xl mx-auto">

    {[
      {
        title: "🤖 AI Based Test Evaluation",
        desc: "Smart analysis of performance, weak areas, and personalized improvement suggestions.",
      },
      {
        title: "📈 Detailed Analytics Dashboard",
        desc: "Graphs, accuracy tracking, time analysis, and progress reports.",
      },
      {
        title: "📚 Public Test Library",
        desc: "Explore thousands of community-created tests across subjects.",
      },
      {
        title: "🏆 Leaderboards & Gamification",
        desc: "Compete with friends, earn ranks, badges, and stay motivated.",
      },
      {
        title: "📱 Mobile App",
        desc: "Android app for learning on the go — anytime, anywhere.",
      },
      {
        title: "🧑‍🤝‍🧑 Group Practice Mode",
        desc: "Create study groups and attempt tests together in real-time.",
      },
    ].map((item, i) => (
      <motion.div
        key={i}
        whileHover={{ y: -6 }}
        className="bg-[#F4EBDD] border border-[#E6D8C3] rounded-2xl p-8 shadow-md"
      >
        <h3 className="text-xl font-semibold mb-3 text-[#7A4A28]">
          {item.title}
        </h3>
        <p className="text-[#5C4632] text-lg">{item.desc}</p>
      </motion.div>
    ))}

  </div>
</section>


      {/* ================= CONTACT ================= */}
      <section className="px-8 py-28 text-center">
        <motion.h2
          className="text-3xl font-bold mb-6"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
        >
          Join the BharatParikhsa Community
        </motion.h2>

        <p className="text-lg text-[#5C4632] mb-12 max-w-3xl mx-auto">
          Whether you want to create tests, practice daily, or help others learn —
          BharatParikhsa is your platform.
        </p>

        <div className="flex flex-col items-center gap-4">
          <a
            href="https://github.com/Vishaljindher"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 px-8 py-4 bg-[#FAF6EF] border border-[#E6D8C3] rounded-xl shadow hover:bg-[#EFE1CC]"
          >
            <FaGithub /> GitHub
          </a>

          <a
            href="https://linkedin.com/in/vishal-jindher-"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 px-8 py-4 bg-[#FAF6EF] border border-[#E6D8C3] rounded-xl shadow hover:bg-[#EFE1CC]"
          >
            <FaLinkedin /> LinkedIn
          </a>

          <a
            href="mailto:yogeshjinder117@gmail.com"
            className="flex items-center gap-4 px-8 py-4 bg-[#FAF6EF] border border-[#E6D8C3] rounded-xl shadow hover:bg-[#EFE1CC]"
          >
            <FaEnvelope /> Email
          </a>
        </div>

        <p className="mt-12 text-lg font-semibold text-[#7A4A28]">
          BharatParikhsa – Testing Knowledge, Building Futures.
        </p>
      </section>
    </div>
  );
}
